import React, { useContext, useEffect, useState } from "react";
import * as Collapsible from "@radix-ui/react-collapsible";
import Button from "@/components/atoms/Button";
import { useFormUtils } from "@/hooks/useFormUtils";
import { FormContext } from "@/contexts/FormDataContext";
import { GlobalContext } from "@/contexts/SiteDataContext";
import { FormStepType } from "@/data/forms/types";
import { Addon } from "@/templates/types";
import { cleanHtmlTags } from "@/utils";
import { isEmptyStep, getFieldData, getIncorrectPrintedPairedField } from "./reviewUtils";
import { getDocumentType } from "@/utils/Form/formatData";
import CheckWithCircle from "@/assets/svg/check-with-circle.svg";
import ExpandIcon from "@/assets/svg/expand.svg";
import "./styles.scss";

const ReviewItem = ({
  step,
  index,
  formSteps,
  defaultOpen = false,
}: {
  step: FormStepType;
  index: number;
  formSteps: FormStepType[];
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(!!defaultOpen);
  const [hasAddonsCard, setHasAddonsCard] = useState<boolean>(false);
  const { t } = useContext(GlobalContext).context;
  const { formData } = useContext(FormContext)!;
  const { isGroupVisible, handleEditGroup } = useFormUtils();

  const isOptional = step.title.includes("Optional");
  const showStep =
    (isOptional && !isEmptyStep({ step, formData, formSteps, isGroupVisible })) || !isOptional;

  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);

  const renderReviewField = ({
    label,
    value,
    isVisible,
    fieldName,
    groupName,
  }: {
    label: string;
    value: string;
    isVisible: boolean;
    fieldName: string;
    groupName: string;
  }) => {
    if (!isVisible) return null;
    return (
      <div
        key={`${fieldName}-${groupName}`}
        className="review__field">
        <p className="review__field__question medium">{label}</p>
        <p className="review__field__answer medium">{value}</p>
      </div>
    );
  };

  const renderGroupFields = (group: any) => {
    const isIncorrectPrintedGroup = group.name === "incorrectPrintedInfoSubGroup";

    let fieldsToRender = isIncorrectPrintedGroup
      ? group.fields
          .filter((field: any) => field.name.includes("Check"))
          .map((field: any) => {
            const pairedField = getIncorrectPrintedPairedField({ field, group });
            return {
              ...getFieldData({ field: pairedField, formData, t }),
              fieldName: field.name,
              groupName: group.name,
            };
          })
      : group.fields.map((field: any) => {
          return {
            ...getFieldData({ field, formData, t }),
            fieldName: field.name,
            groupName: group.name,
          };
        });

    // Adding subgroups fields when needed (Passports & Cards)
    if (group.subGroups) {
      const subgroupFieldsToRender = group.subGroups.flatMap((subgroup: any) => {
        const {
          condition: { field: conditionField, values, name },
          fields,
        } = subgroup;
        const conditionFieldValue = formData[conditionField];

        if (values?.includes(conditionFieldValue)) {
          return (
            fields?.map((field: any) => {
              return {
                ...getFieldData({ field, formData, t }),
                fieldName: field.name,
                groupName: name,
              };
            }) || []
          );
        }

        return [];
      });

      if (subgroupFieldsToRender.length > 0) {
        fieldsToRender = [...fieldsToRender, ...subgroupFieldsToRender];
      }
    }

    return fieldsToRender
      .filter(({ isVisible }: { isVisible: boolean }) => isVisible)
      .map(
        ({
          label,
          value,
          fieldName,
          groupName,
        }: {
          label: string;
          value: string;
          fieldName: string;
          groupName: string;
        }) => renderReviewField({ label, value, isVisible: true, fieldName, groupName })
      );
  };

  useEffect(() => {
    const addonCardItem = formData.addons.filter((addon: Addon) => addon.identifier === "card");
    const cardIsSelected = addonCardItem.length && addonCardItem[0].value === "1";
    setHasAddonsCard(cardIsSelected);
  }, [formData]);

  return (
    <Collapsible.Root
      id={step.name}
      className="review__item"
      open={isOpen}
      onOpenChange={setIsOpen}>
      <Collapsible.Trigger
        className="review__item-trigger"
        data-qa={`review-section-${step.name}`}>
        <h2 className="review__item-title big">{cleanHtmlTags(t[step.title])}</h2>
        <div className="review__item-right">
          <div className="review__item-status">
            <span>{t["Completed"]}</span>
            <CheckWithCircle
              width={22}
              height={22}
            />
          </div>
          <ExpandIcon
            width={12}
            height={12}
            className="review__item-expandIcon"
          />
        </div>
      </Collapsible.Trigger>

      <Collapsible.Content className="review__item-content">
        {showStep &&
          step.groups
            .filter((group) =>
              isGroupVisible({ groupName: group.name, formValues: formData, formSteps })
            )
            .map((group: any) => {
              return (
                <div key={`step${index}-${group.name}`}>
                  {hasAddonsCard && group.name === "passport format" && (
                    <div
                      key={`documentType-${group.name}`}
                      className="review__field">
                      <p className="review__field__question medium">
                        {t["Requested Document Type"]}
                      </p>
                      <p className="review__field__answer medium">{getDocumentType(formData)}</p>
                    </div>
                  )}
                  {renderGroupFields(group)}
                </div>
              );
            })}
        {!showStep && (
          <p className="review__emptyCopy">
            {t["For the moment you have not added any "]} {t[step.name.replace("-", " ")]}
          </p>
        )}
        <div className="review__item__button">
          <Button
            tertiary
            dataQa={`edit-${step.name}`}
            label={t["Edit section"]}
            onClick={() => handleEditGroup(index)}
          />
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

export default ReviewItem;
